import React, { useState, useEffect } from 'react';
import { Search, DollarSign, Receipt, AlertCircle } from 'lucide-react';
import api from '@/api/api';
import Select from 'react-select';
import BankSelector from '@/components/BankSelector';
import { formatDate } from '../../utils/dateTimeUtils';

const PayOtherFee = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [applicableFees, setApplicableFees] = useState([]);
  const [selectedFees, setSelectedFees] = useState([]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [selectedBank, setSelectedBank] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await api.get(`/students/dropdown`);
      const studentsData = Array.isArray(response.data) ? response.data : [];
      const approvedStudents = studentsData.filter(s => s.is_approved);
      setStudents(approvedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const studentOptions = students.map(student => ({
    value: student.id,
    label: `${student.admission_number} - ${student.full_name}`,
    student: student,
    searchText: `${student.admission_number} ${student.full_name} ${student.phone || ''} ${student.email || ''}`.toLowerCase()
  }));

  const customFilterOption = (option, inputValue) => {
    if (!inputValue) return true;
    const searchTerms = inputValue.toLowerCase().trim().split(' ');
    return searchTerms.every(term => option.data.searchText.includes(term));
  };

  const handleStudentChange = async (studentId) => {
    setSelectedStudentId(studentId);
    const student = students.find(s => s.id === studentId);
    setSelectedStudent(student);
    setSelectedFees([]);

    if (student) {
      await fetchApplicableFees(student);
    } else {
      setApplicableFees([]);
    }
  };

  const fetchApplicableFees = async (student) => {
    try {
      const feesResponse = await api.get(`/master-settings/other-fees`);
      const paymentsResponse = await api.get(`/fees/other-fee-payments?student_id=${student.id}`);

      const feesData = Array.isArray(feesResponse.data) ? feesResponse.data : [];
      const paymentsData = Array.isArray(paymentsResponse.data) ? paymentsResponse.data : [];
      const paidFeeIds = paymentsData.map(p => p.other_fee_id);

      let admissionType = 'Regular';
      if (student.session && student.session.toUpperCase().includes('LATERAL')) {
        admissionType = 'Lateral';
      }

      const applicable = feesData.filter(fee => {
        if (fee.is_active === false) return false;
        const courseMatch = !fee.course_id || fee.course_id === '' || fee.course_id === student.course_id;
        const sessionMatch = true;
        const typeMatch = !fee.applicable_to || 
                         fee.applicable_to === 'Both' || 
                         (fee.applicable_to === 'Regular' && admissionType === 'Regular') ||
                         (fee.applicable_to === 'Lateral' && admissionType === 'Lateral');
        return courseMatch && sessionMatch && typeMatch;
      }).map(fee => ({
        ...fee,
        is_paid: paidFeeIds.includes(fee.id)
      }));

      setApplicableFees(applicable);
    } catch (error) {
      console.error('Error fetching applicable fees:', error);
      alert('Failed to fetch applicable fees');
    }
  };

  const handleFeeSelection = (fee, isChecked) => {
    if (isChecked) {
      const fineToPay = (fee.late_fine && isOverdue(fee.payment_date)) ? parseFloat(fee.late_fine) || 0 : 0;
      setSelectedFees([...selectedFees, { ...fee, paying_amount: parseFloat(fee.amount) || 0, fine_paid: fineToPay }]);
    } else {
      setSelectedFees(selectedFees.filter(f => f.id !== fee.id));
    }
  };

  const handleAmountChange = (feeId, amount) => {
    setSelectedFees(selectedFees.map(f => 
      f.id === feeId ? { ...f, paying_amount: parseFloat(amount) || 0 } : f
    ));
  };

  const handleFineChange = (feeId, fine) => {
    setSelectedFees(selectedFees.map(f => 
      f.id === feeId ? { ...f, fine_paid: parseFloat(fine) || 0 } : f
    ));
  };

  const getTotalAmount = () => {
    return selectedFees.reduce((sum, fee) => sum + (parseFloat(fee.paying_amount) || 0) + (parseFloat(fee.fine_paid) || 0), 0);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (selectedFees.length === 0) {
      alert('Please select at least one fee to pay');
      return;
    }

    if (paymentMode !== 'Cash' && !selectedBank) {
      alert('Please select a bank for non-cash payment');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        student_id: selectedStudent.id,
        fees: selectedFees.map(fee => ({
          other_fee_id: fee.id,
          other_fee_name: fee.name,
          course: selectedStudent.course_name,
          session: selectedStudent.session,
          amount: fee.paying_amount,
          fine_paid: fee.fine_paid || 0,
          type: fee.is_mandatory ? 'Mandatory' : 'Optional',
          description: fee.description || ''
        })),
        payment_mode: paymentMode,
        bank_id: paymentMode !== 'Cash' ? selectedBank : null,
        reference_number: referenceNumber,
        payment_date: new Date().toISOString().split('T')[0]
      };

      await api.post(`/fees/pay-other-fees`, payload);

      alert('Payment recorded successfully!');
      setSelectedFees([]);
      setPaymentMode('Cash');
      setSelectedBank('');
      setReferenceNumber('');
      handleStudentChange(selectedStudentId);
    } catch (error) {
      console.error('Error recording payment:', error);
      alert(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen" data-testid="pay-other-fee-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Pay Other Fee</h1>
          <p className="text-gray-600 mt-2">Pay library, exam, sports, and other miscellaneous fees</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Student *
          </label>
          <Select
            data-testid="student-select"
            value={studentOptions.find(opt => opt.value === selectedStudentId) || null}
            onChange={(selectedOption) => handleStudentChange(selectedOption ? selectedOption.value : '')}
            options={studentOptions}
            filterOption={customFilterOption}
            placeholder="Search by name, admission number, mobile, or email..."
            isClearable
            isSearchable
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '42px',
                borderColor: '#d1d5db',
                '&:hover': { borderColor: '#9ca3af' }
              }),
              menu: (base) => ({ ...base, zIndex: 50 })
            }}
            formatOptionLabel={(option) => (
              <div>
                <div className="font-semibold text-gray-900">{option.label}</div>
                <div className="text-sm text-gray-600">
                  {option.student.course_name} | {option.student.session}
                </div>
              </div>
            )}
          />
        </div>

        {selectedStudent && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">Student Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-600">Name:</span>
                <p className="font-medium">{selectedStudent.full_name}</p>
              </div>
              <div>
                <span className="text-blue-600">Admission No:</span>
                <p className="font-medium">{selectedStudent.admission_number}</p>
              </div>
              <div>
                <span className="text-blue-600">Course:</span>
                <p className="font-medium">{selectedStudent.course_name}</p>
              </div>
              <div>
                <span className="text-blue-600">Session:</span>
                <p className="font-medium">{selectedStudent.session}</p>
              </div>
            </div>
          </div>
        )}

        {selectedStudent && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4">Applicable Other Fees</h3>
            
            {applicableFees.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="mx-auto mb-2" size={48} />
                <p>No applicable fees found for this student</p>
              </div>
            ) : (
              <div className="space-y-3">
                {applicableFees.map(fee => (
                  <div key={fee.id} className={`border rounded-lg p-4 ${fee.is_paid ? 'bg-gray-100 opacity-75' : 'hover:bg-gray-50'}`}>
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={selectedFees.some(f => f.id === fee.id)}
                        onChange={(e) => handleFeeSelection(fee, e.target.checked)}
                        disabled={fee.is_paid}
                        className="mt-1 h-5 w-5 text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">{fee.name}</h4>
                              {fee.is_paid && (
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
                                  Already Paid
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{fee.description}</p>
                            <div className="flex gap-4 mt-2 text-sm">
                              <span className={`px-2 py-1 rounded text-xs ${
                                fee.is_mandatory ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {fee.is_mandatory ? 'Mandatory' : 'Optional'}
                              </span>
                              {fee.payment_date && !fee.is_paid && (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  isOverdue(fee.payment_date) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  Due: {formatDate(fee.payment_date)}
                                  {isOverdue(fee.payment_date) && ' (OVERDUE)'}
                                </span>
                              )}
                              {fee.late_fine && isOverdue(fee.payment_date) && !fee.is_paid && (
                                <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">
                                  Late Fine: {fee.late_fine}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${fee.is_paid ? 'text-gray-400' : 'text-green-600'}`}>
                              {(parseFloat(fee.amount) || 0).toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>
                        
                        {selectedFees.some(f => f.id === fee.id) && !fee.is_paid && (
                          <div className="mt-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium text-gray-700">Paying Amount:</label>
                              <input
                                type="number"
                                value={selectedFees.find(f => f.id === fee.id)?.paying_amount || fee.amount}
                                onChange={(e) => handleAmountChange(fee.id, e.target.value)}
                                className="w-32 px-3 py-1 border border-gray-300 rounded"
                                min="0"
                                step="0.01"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium text-gray-700">Late Fine:</label>
                              <input
                                type="number"
                                value={selectedFees.find(f => f.id === fee.id)?.fine_paid || 0}
                                onChange={(e) => handleFineChange(fee.id, e.target.value)}
                                className="w-32 px-3 py-1 border border-yellow-300 rounded bg-yellow-50"
                                min="0"
                                step="0.01"
                              />
                              <span className="text-xs text-gray-500">
                                Total: {((parseFloat(selectedFees.find(f => f.id === fee.id)?.paying_amount) || 0) + (parseFloat(selectedFees.find(f => f.id === fee.id)?.fine_paid) || 0)).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedFees.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Payment Details</h3>
            <form onSubmit={handleSubmitPayment}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Mode *
                  </label>
                  <select
                    data-testid="payment-mode-select"
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="Net Banking">Net Banking</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Demand Draft">Demand Draft</option>
                  </select>
                </div>

                <BankSelector 
                  paymentMode={paymentMode}
                  selectedBank={selectedBank}
                  onBankChange={setSelectedBank}
                  required={true}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Transaction/Cheque number"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-lg font-semibold">Total Amount:</span>
                  <span className="text-2xl font-bold text-green-600">{getTotalAmount().toLocaleString('en-IN')}</span>
                </div>
                <p className="text-sm text-gray-600">({selectedFees.length} fee(s) selected)</p>
              </div>

              <button
                type="submit"
                data-testid="submit-payment-btn"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <DollarSign size={20} />
                {loading ? 'Processing...' : 'Pay Other Fee'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayOtherFee;
